import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../../../components/ContentBox';
import { listProfileReports, getProfileReport } from '../../../services/api';

const API_BASE = `${process.env.REACT_APP_API_URL}/profile-reports`;

// Set to true to bypass the staff-protection block during testing.
// MUST be false in production.
const DEV_BYPASS_STAFF_BLOCK = false;

async function apiPost(path, body) {
    const res = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
    });
    return res.json();
}

const DURATION_OPTIONS = [
    { value: '1h', label: '1 Hour' },
    { value: '4h', label: '4 Hours' },
    { value: '12h', label: '12 Hours' },
    { value: '1d', label: '1 Day' },
    { value: '1w', label: '1 Week' },
    { value: '1m', label: '1 Month' }
];

const BAN_DURATION_OPTIONS = [
    ...DURATION_OPTIONS,
    { value: 'permanent', label: 'Permanent' }
];

const ACTION_BADGE = {
    'ban':       'bg-red-100 text-red-700 border border-red-300',
    'restrict':  'bg-amber-100 text-amber-700 border border-amber-300',
    'no-action': 'bg-gray-100 text-gray-600 border border-gray-300',
};

function ActionPanel({ selected, onClose, onResolved }) {
    const [mode, setMode] = useState(null);
    const [staffNotes, setStaffNotes] = useState('');
    const [restrictDuration, setRestrictDuration] = useState('1d');
    const [restrictPosting, setRestrictPosting] = useState(false);
    const [restrictCommenting, setRestrictCommenting] = useState(false);
    const [restrictLiking, setRestrictLiking] = useState(false);
    const [restrictCustomisation, setRestrictCustomisation] = useState(false);
    const [resetCustomisation, setResetCustomisation] = useState(false);
    const [deletePosts, setDeletePosts] = useState(false);
    const [deleteComments, setDeleteComments] = useState(false);
    const [banDuration, setBanDuration] = useState('1d');
    const [submitting, setSubmitting] = useState(false);

    const targetName = selected?.targetHabboName || selected?.targetUserID || '';

    const handleNoAction = async () => {
        setSubmitting(true);
        try {
            const r = await apiPost(`${API_BASE}/${selected.id}/action/no-action`, { staffNotes });
            if (r?.success) {
                toast.success('Report closed with no action.');
                onResolved();
            } else {
                toast.error(r?.message || 'Failed.');
            }
        } catch {
            toast.error('Failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRestrict = async () => {
        if (!restrictPosting && !restrictCommenting && !restrictLiking && !restrictCustomisation) {
            toast.error('Select at least one restriction.');
            return;
        }
        setSubmitting(true);
        try {
            const r = await apiPost(`${API_BASE}/${selected.id}/action/restrict`, {
                duration: restrictDuration,
                restrictPosting,
                restrictCommenting,
                restrictLiking,
                restrictCustomisation,
                resetCustomisation,
                deletePosts,
                deleteComments,
                staffNotes
            });
            if (r?.success) {
                toast.success('Restrictions applied and report closed.');
                onResolved();
            } else {
                toast.error(r?.message || 'Failed.');
            }
        } catch {
            toast.error('Failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBan = async () => {
        setSubmitting(true);
        try {
            const r = await apiPost(`${API_BASE}/${selected.id}/action/ban`, {
                duration: banDuration,
                staffNotes
            });
            if (r?.success) {
                toast.success('Ban applied and report closed.');
                onResolved();
            } else {
                toast.error(r?.message || 'Failed.');
            }
        } catch {
            toast.error('Failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = 'border border-black/30 bg-offwhite px-2 py-1 text-sm text-coal rounded outline-none';
    const btnCls = 'px-3 py-1 border border-black/40 bg-offwhite text-coal text-sm rounded hover:bg-black/5 disabled:opacity-50';
    const checkRow = (label, checked, onChange) => (
        <label className="flex items-center gap-2 text-sm text-coal cursor-pointer select-none">
            <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="accent-gold" />
            {label}
        </label>
    );

    return (
        <div className="border border-black/20 rounded bg-offwhite p-3 flex flex-col gap-3">
            <div className="text-sm font-bold text-coal">Action for: {targetName}</div>

            {!mode && (
                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setMode('none')} className={btnCls}>No Action</button>
                    <button type="button" onClick={() => setMode('restrict')} className={btnCls}>Restrict</button>
                    <button type="button" onClick={() => setMode('ban')} className="px-3 py-1 border border-red-400 bg-red-50 text-red-700 text-sm rounded hover:bg-red-100">Profile Ban</button>
                    <button type="button" onClick={onClose} className={btnCls}>Cancel</button>
                </div>
            )}

            {mode === 'none' && (
                <div className="flex flex-col gap-2">
                    <div className="text-xs text-coal/70">Close the ticket with no action. The reporter will receive an inbox message.</div>
                    <textarea
                        placeholder="Staff notes (optional)"
                        value={staffNotes}
                        onChange={e => setStaffNotes(e.target.value)}
                        rows={2}
                        className={`${inputCls} resize-none w-full`}
                    />
                    <div className="flex gap-2">
                        <button type="button" disabled={submitting} onClick={handleNoAction} className={btnCls}>Confirm No Action</button>
                        <button type="button" onClick={() => setMode(null)} className={btnCls}>Back</button>
                    </div>
                </div>
            )}

            {mode === 'restrict' && (
                <div className="flex flex-col gap-2">
                    <div className="text-xs text-coal/70">Apply feature restrictions to the reported user. Both reporter and reportee will receive an inbox message.</div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-coal font-bold shrink-0">Duration:</span>
                        <select value={restrictDuration} onChange={e => setRestrictDuration(e.target.value)} className={inputCls}>
                            {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div className="border border-black/15 rounded p-2 flex flex-col gap-1">
                        <div className="text-xs font-bold text-coal/60 uppercase mb-1">Feature Restrictions</div>
                        {checkRow('Posting', restrictPosting, setRestrictPosting)}
                        {checkRow('Commenting (own & others pages)', restrictCommenting, setRestrictCommenting)}
                        {checkRow('Liking', restrictLiking, setRestrictLiking)}
                        {checkRow('Profile Customisation', restrictCustomisation, setRestrictCustomisation)}
                    </div>
                    <div className="border border-black/15 rounded p-2 flex flex-col gap-1">
                        <div className="text-xs font-bold text-coal/60 uppercase mb-1">Additional Actions</div>
                        {checkRow('Reset profile customisation', resetCustomisation, setResetCustomisation)}
                        {checkRow('Delete all posts', deletePosts, setDeletePosts)}
                        {checkRow('Delete all comments/replies', deleteComments, setDeleteComments)}
                    </div>
                    <textarea
                        placeholder="Staff notes (optional)"
                        value={staffNotes}
                        onChange={e => setStaffNotes(e.target.value)}
                        rows={2}
                        className={`${inputCls} resize-none w-full`}
                    />
                    <div className="flex gap-2">
                        <button type="button" disabled={submitting} onClick={handleRestrict} className={btnCls}>Apply Restrictions</button>
                        <button type="button" onClick={() => setMode(null)} className={btnCls}>Back</button>
                    </div>
                </div>
            )}

            {mode === 'ban' && (
                <div className="flex flex-col gap-2">
                    <div className="text-xs text-coal/70">Ban the reported user from Profiles entirely. Both reporter and reportee will receive an inbox message.</div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-coal font-bold shrink-0">Duration:</span>
                        <select value={banDuration} onChange={e => setBanDuration(e.target.value)} className={inputCls}>
                            {BAN_DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <textarea
                        placeholder="Staff notes (optional)"
                        value={staffNotes}
                        onChange={e => setStaffNotes(e.target.value)}
                        rows={2}
                        className={`${inputCls} resize-none w-full`}
                    />
                    <div className="flex gap-2">
                        <button type="button" disabled={submitting} onClick={handleBan} className="px-3 py-1 border border-red-400 bg-red-50 text-red-700 text-sm rounded hover:bg-red-100 disabled:opacity-50">Apply Ban</button>
                        <button type="button" onClick={() => setMode(null)} className={btnCls}>Back</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProfileReports({ userData }) {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [selected, setSelected] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('open');
    const [section, setSection] = useState('profileData');
    const [showActionPanel, setShowActionPanel] = useState(false);
    const [claiming, setClaiming] = useState(false);

    // userData.hasStaffRole and hasAdminRole come from the auth token / session.
    // isStaffOnly = staff role but NOT admin role.
    const isAdmin = !!(userData?.hasAdminRole);
    const isStaffOnly = !!(userData?.hasStaffRole) && !isAdmin;

    const loadReports = async (keepSelected = false) => {
        setLoading(true);
        try {
            const r = await listProfileReports(200, 0, statusFilter);
            if (r?.success) {
                const list = Array.isArray(r.reports) ? r.reports : [];
                setReports(list);

                if (!keepSelected) {
                    const current = String(selectedId || '');
                    const stillExists = current && list.some(x => String(x?.id || '') === current);
                    if (!stillExists) {
                        const next = list.length > 0 ? String(list[0].id || '') : '';
                        setSelectedId(next);
                        if (!next) setSelected(null);
                    }
                }
            } else {
                toast.error(r?.message || 'Failed to load reports.');
            }
        } catch {
            toast.error('Failed to load reports.');
        } finally {
            setLoading(false);
        }
    };

    const loadReport = async (id) => {
        const rid = String(id || '').trim();
        if (!rid) { setSelected(null); return; }
        setDetailLoading(true);
        try {
            const r = await getProfileReport(rid);
            if (r?.success) {
                setSelected(r.report || null);
                setShowActionPanel(false);
            } else {
                toast.error(r?.message || 'Failed to load report.');
            }
        } catch {
            toast.error('Failed to load report.');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleClaim = async () => {
        if (!selected) return;
        const claimedId = String(selected.id);
        setClaiming(true);
        try {
            const r = await apiPost(`${API_BASE}/${selected.id}/claim`, {});
            if (r?.success) {
                toast.success('Report claimed.');
                await loadReport(claimedId);
                setStatusFilter('reviewing');
            } else {
                toast.error(r?.message || 'Failed to claim.');
            }
        } catch {
            toast.error('Failed to claim.');
        } finally {
            setClaiming(false);
        }
    };

    const handleResolved = async () => {
        setShowActionPanel(false);
        setSelected(null);
        setSelectedId('');
        await loadReports();
    };

    useEffect(() => { loadReports(); }, [statusFilter]);
    useEffect(() => { if (selectedId) loadReport(selectedId); }, [selectedId]);

    const snapshot = selected?.snapshot || null;
    const profileData = useMemo(() => Array.isArray(snapshot?.profileData) ? snapshot.profileData : [], [snapshot]);
    const posts = useMemo(() => Array.isArray(snapshot?.posts) ? snapshot.posts : [], [snapshot]);
    const commentsOnPosts = useMemo(() => Array.isArray(snapshot?.commentsOnPosts) ? snapshot.commentsOnPosts : [], [snapshot]);
    const commentsByUser = useMemo(() => Array.isArray(snapshot?.commentsByUser) ? snapshot.commentsByUser : [], [snapshot]);

    const sectionData = useMemo(() => {
        if (section === 'posts') return posts;
        if (section === 'commentsOnPosts') return commentsOnPosts;
        if (section === 'commentsByUser') return commentsByUser;
        return profileData;
    }, [section, posts, commentsOnPosts, commentsByUser, profileData]);

    // Guard: only staff or admin may access this page
    if (!userData?.hasStaffRole && !userData?.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const selectedIdStr = String(selectedId || '');
    const isReviewing = selected?.status === 'reviewing';
    const isOpen = selected?.status === 'open';
    const isClosed = selected?.status === 'closed';

    // targetIsStaff is returned by the backend as TINYINT(1) — use Number() to
    // coerce correctly (plain !! would make "0" truthy).
    const targetIsStaff = Number(selected?.targetIsStaff) > 0;

    // Staff-only users (not admin) cannot claim or handle reports against other staff/admin.
    // DEV_BYPASS_STAFF_BLOCK disables this check for testing.
    const claimBlocked = !DEV_BYPASS_STAFF_BLOCK && isStaffOnly && targetIsStaff;

    const btnCls = 'px-3 py-1 border border-black/40 bg-offwhite text-coal text-sm rounded hover:bg-black/5 disabled:opacity-50';

    return (
        <div className="w-full min-h-screen p-4 lg2:p-6">
            {DEV_BYPASS_STAFF_BLOCK && (
                <div className="mb-4 px-3 py-2 bg-yellow-100 border border-yellow-400 text-yellow-800 text-xs font-bold rounded">
                    ⚠ DEV_BYPASS_STAFF_BLOCK is ON, staff protection is disabled. Set to false before deploying.
                </div>
            )}
            <div className="grid grid-cols-1 lg2:grid-cols-[320px_minmax(0,1fr)] gap-4 lg2:gap-6 lg2:items-start">

                {/* ── Left column — sticky, fixed height, scrolls internally ── */}
                <div className="lg2:sticky lg2:top-6 flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 48px)' }}>
                    <ContentBox theme="default" header="Reports" padding="p-0">
                        {/* Status filter + refresh */}
                        <div className="border-b border-black/20 p-2 flex items-center gap-2 shrink-0">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="flex-1 border border-black/30 bg-offwhite px-2 py-1 text-sm text-coal rounded outline-none"
                            >
                                <option value="open">Open</option>
                                <option value="reviewing">Reviewing</option>
                                <option value="closed">Closed</option>
                            </select>
                            <button type="button" onClick={() => loadReports()} className={btnCls}>Refresh</button>
                        </div>

                        {/* Scrollable list — height fills remaining space after the header + filter bar */}
                        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(100vh - 140px)' }}>
                            {loading ? (
                                <div className="p-3 text-coal/70">Loading…</div>
                            ) : reports.length === 0 ? (
                                <div className="p-3 text-coal/70">No reports.</div>
                            ) : (
                                reports.map(r => {
                                    const rid = String(r?.id || '');
                                    const active = rid === selectedIdStr;
                                    const target = r?.targetHabboName || r?.targetUserID || '';
                                    const reason = r?.reason || '';
                                    const reporter = r?.reporterHabboName || r?.reporterUserID || '';
                                    const listTargetIsStaff = Number(r?.targetIsStaff) > 0;

                                    let rowCls = 'bg-offwhite hover:bg-black/5';
                                    if (active) rowCls = 'bg-gold/30';
                                    else if (listTargetIsStaff) rowCls = 'bg-purple-50 hover:bg-purple-100';

                                    return (
                                        <button
                                            key={rid}
                                            type="button"
                                            onClick={() => setSelectedId(rid)}
                                            className={`w-full text-left px-3 py-2 border-b border-black/10 ${rowCls}`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <div className={`font-bold text-sm truncate flex items-center gap-1 ${listTargetIsStaff ? 'text-purple-700' : 'text-coal'}`}>
                                                    #{rid} {target}
                                                    {listTargetIsStaff && (
                                                        <span className="text-[9px] uppercase font-bold bg-purple-200 text-purple-700 px-1 py-0.5 rounded shrink-0">Staff</span>
                                                    )}
                                                </div>
                                                <div className="text-[10px] uppercase font-bold text-coal/50 shrink-0">{r?.status || ''}</div>
                                            </div>
                                            <div className="text-xs text-coal/80 truncate">{reason}</div>
                                            <div className="text-[10px] text-coal/50 truncate">By {reporter}</div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </ContentBox>
                </div>

                {/* ── Right column — detail ── */}
                <div className="w-full flex flex-col gap-4 min-h-0">

                    {/* Toolbar */}
                    <ContentBox theme="default" header="Profile Reports" padding="p-3">
                        <div className="flex flex-wrap gap-2 items-center">
                            <select
                                value={selectedId}
                                onChange={(e) => setSelectedId(e.target.value)}
                                className="min-w-[260px] flex-1 border border-black/30 bg-offwhite px-2 py-1 text-sm text-coal rounded outline-none"
                            >
                                {reports.map(r => (
                                    <option key={r.id} value={String(r.id)}>
                                        #{r.id} {r.targetHabboName || r.targetUserID}: {r.reason}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={section}
                                onChange={(e) => setSection(e.target.value)}
                                className="border border-black/30 bg-offwhite px-2 py-1 text-sm text-coal rounded outline-none"
                            >
                                <option value="profileData">Profile Data</option>
                                <option value="posts">Posts</option>
                                <option value="commentsOnPosts">Comments On Posts</option>
                                <option value="commentsByUser">Comments By User</option>
                            </select>
                            <button type="button" onClick={() => loadReports()} className={btnCls}>Refresh</button>
                        </div>
                    </ContentBox>

                    {/* Detail panel */}
                    <ContentBox theme="default" header="Report Detail" padding="p-3">
                        {detailLoading ? (
                            <div className="text-coal/70">Loading…</div>
                        ) : !selected ? (
                            <div className="text-coal/70">Select a report.</div>
                        ) : (
                            <div className="flex flex-col gap-3">

                                {/* Staff-target warning */}
                                {targetIsStaff && (
                                    <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded px-3 py-2 text-sm text-purple-700 font-bold">
                                        ⚠ This report is about a staff member.
                                        {isStaffOnly && ' Only an admin can handle this report.'}
                                    </div>
                                )}

                                {/* Report metadata */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-coal">
                                    <div><span className="font-bold">ID:</span> {selected.id}</div>
                                    <div className="flex items-center gap-1">
                                        <span className="font-bold">Status:</span>
                                        <span className="capitalize">{selected.status}</span>
                                    </div>

                                    {/* Reporter: HabboName preferred, ID as fallback */}
                                    <div>
                                        <span className="font-bold">Reporter:</span>{' '}
                                        {selected.reporterHabboName || selected.reporterUserID}
                                    </div>

                                    {/* Target: HabboName preferred, ID as fallback + staff badge */}
                                    <div className="flex items-center gap-1 flex-wrap">
                                        <span className="font-bold">Target:</span>
                                        <span>{selected.targetHabboName || selected.targetUserID}</span>
                                        {targetIsStaff && (
                                            <span className="text-[9px] uppercase font-bold bg-purple-200 text-purple-700 px-1 py-0.5 rounded">Staff</span>
                                        )}
                                    </div>

                                    <div className="md:col-span-2">
                                        <span className="font-bold">Reason:</span> {selected.reason}
                                    </div>
                                    <div className="md:col-span-2">
                                        <span className="font-bold">Details:</span> {selected.details || '-'}
                                    </div>
                                    <div className="md:col-span-2">
                                        <span className="font-bold">Submitted:</span>{' '}
                                        {selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '-'}
                                    </div>
                                    <div className="md:col-span-2">
                                        <span className="font-bold">Snapshot captured:</span>{' '}
                                        {snapshot?.capturedAt ? new Date(snapshot.capturedAt).toLocaleString() : '-'}
                                    </div>

                                    {/* Claimed by: HabboName preferred */}
                                    {selected.claimedByUserID && (
                                        <div className="md:col-span-2">
                                            <span className="font-bold">Claimed by:</span>{' '}
                                            {selected.claimedByHabboName || selected.claimedByUserID}
                                        </div>
                                    )}

                                    {/* Handled by: HabboName preferred */}
                                    {isClosed && selected.handledByUserID && (
                                        <div className="md:col-span-2">
                                            <span className="font-bold">Handled by:</span>{' '}
                                            {selected.handledByHabboName || selected.handledByUserID}
                                        </div>
                                    )}
                                    {isClosed && selected.handledAt && (
                                        <div className="md:col-span-2">
                                            <span className="font-bold">Handled at:</span>{' '}
                                            {new Date(selected.handledAt).toLocaleString()}
                                        </div>
                                    )}

                                    {/* Outcome — ban / restrict / no-action badge + detail text */}
                                    {isClosed && selected.actionType && (
                                        <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
                                            <span className="font-bold">Outcome:</span>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${ACTION_BADGE[selected.actionType] || ACTION_BADGE['no-action']}`}>
                                                {selected.actionType === 'ban'
                                                    ? 'Ban'
                                                    : selected.actionType === 'restrict'
                                                        ? 'Restriction'
                                                        : 'No Action'}
                                            </span>
                                            <span className="text-xs text-coal/70">{selected.actionDetail}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Staff notes — shown whenever present, regardless of status */}
                                {selected.staffNotes && (
                                    <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                        <div className="font-bold text-amber-800 text-sm mb-1">Staff Notes</div>
                                        <div className="text-amber-900 text-sm">{selected.staffNotes}</div>
                                    </div>
                                )}

                                {/* Action buttons */}
                                {!isClosed && (
                                    <div className="flex flex-wrap gap-2 items-center">
                                        {isOpen && (
                                            <>
                                                <button
                                                    type="button"
                                                    disabled={claiming || claimBlocked}
                                                    onClick={handleClaim}
                                                    title={claimBlocked ? 'Only an admin can claim reports about staff members.' : undefined}
                                                    className="px-3 py-1 border border-blue-400 bg-blue-50 text-blue-700 text-sm rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {claiming ? 'Claiming…' : 'Claim Ticket'}
                                                </button>
                                                {claimBlocked && (
                                                    <span className="text-xs text-purple-600">Admin only, target is a staff member.</span>
                                                )}
                                            </>
                                        )}
                                        {isReviewing && !showActionPanel && (
                                            claimBlocked ? (
                                                <span className="text-xs text-purple-600">Admin only, target is a staff member.</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowActionPanel(true)}
                                                    className="px-3 py-1 border border-green-500 bg-green-50 text-green-700 text-sm rounded hover:bg-green-100"
                                                >
                                                    Handle Report
                                                </button>
                                            )
                                        )}
                                    </div>
                                )}

                                {showActionPanel && isReviewing && (
                                    <ActionPanel
                                        selected={selected}
                                        onClose={() => setShowActionPanel(false)}
                                        onResolved={handleResolved}
                                    />
                                )}

                                {/* Snapshot viewer */}
                                <div className="border-t border-black/20" />
                                <div className="text-xs text-coal/60">
                                    Snapshot: {section} ({sectionData.length} items)
                                </div>
                                <div className="max-h-[520px] overflow-y-auto border border-black/20 bg-offwhite rounded">
                                    <pre className="text-[11px] leading-snug text-coal whitespace-pre-wrap break-words p-2 m-0">
                                        {JSON.stringify(sectionData, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </ContentBox>
                </div>
            </div>
        </div>
    );
}

export default ProfileReports;